import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { Dialog } from "component/shared/dialog";
import { DialogTitle } from "component/shared/dialog/title";
import { useAppThunkDispatch } from "redux/store";
import { createCampaignDisplayCategory } from "redux/slice/campaign-display-categories";
import { styles } from "./style";

interface OwnProps {
	open: boolean;
	onClose: () => void;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

/**
 * MAX-3878 — Add Category modal (figma 1551:16947). Copy verbatim: title "Add Category",
 * field "Name*" (required), buttons "Cancel" / "Add Category". The new category merges into the
 * slice; the selector re-sorts, so it lands in its alphabetical accordion position (AM3).
 */
const Component: React.FC<Props> = ({ open, onClose, classes = {} }) => {
	const [name, setName] = useState("");
	const [submitted, setSubmitted] = useState(false);
	const dispatch = useAppThunkDispatch();

	useEffect(() => {
		if (!open) {
			setName("");
			setSubmitted(false);
		}
	}, [open]);

	const nameError = submitted && !name.trim();

	const handleAdd = () => {
		setSubmitted(true);
		if (!name.trim()) {
			return;
		}
		dispatch(createCampaignDisplayCategory({ name: name.trim() }));
		onClose();
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle title="Add Category" onClose={onClose} />
			<Mui.DialogContent>
				<Mui.TextField
					className={classes.field}
					variant="outlined"
					margin="dense"
					label="Name*"
					value={name}
					error={nameError}
					helperText={nameError ? "Name is required." : undefined}
					onChange={(e) => setName(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							handleAdd();
						}
					}}
					fullWidth
					autoFocus
				/>
			</Mui.DialogContent>
			<Mui.DialogActions className={classes.actions}>
				<Mui.Button variant="outlined" color="primary" onClick={onClose}>
					Cancel
				</Mui.Button>
				<Mui.Button variant="contained" color="primary" onClick={handleAdd}>
					Add Category
				</Mui.Button>
			</Mui.DialogActions>
		</Dialog>
	);
};

export const AddCategoryDialog = withStyles(Component, styles);
